import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SEO from 'next-seo.config';
import { NextSeo } from 'next-seo';
import moment from 'moment';
import ReactDOMServer from 'react-dom/server';
import { getReq } from 'api';
import { MAX_PAGE_SIZE } from 'data/globalConstants';

import { wrapper } from 'store';
import { authSelector } from 'store/auth';
import { companySelector, fetchCompanyBySlug } from 'store/company';
import { fetchExperts } from 'store/company/expert';
import {
    closeAlert,
    fetchAlertBySlug,
    fetchResponses,
    removeAlert,
    revealAlertAuthor,
    updateAlert,
} from 'store/alert';
import { handleHideSidescreen } from 'store/ux';

import pluralize from 'utils/pluralize';
import truncate from 'utils/text-truncate';
import tagRemove from 'utils/tag-remove';
import pageTitle, { articleTitle } from 'utils/seo-title';
import { alertRevalidate } from 'utils/revalidate';
import { liveSponsor, sponsorRevalidate } from 'utils/story-sponsor';
import { notFound, notFoundOrThrow } from 'utils/isr';
import buildNewsArticleSchema from 'utils/news-article-schema';
import { youtubeUploadDates } from 'utils/youtube-upload-date';
import { publicAlert, publicResponses } from 'utils/public-alert';
import { jsonLd } from 'utils/safe-html';
import { alertPath, alertUrl } from 'utils/alert-url';
import { common, news_alerts } from 'lang/en';
import { apiErrorMessage } from 'api/alert-slug';
import {
    fetchResearchSponsorship,
    fetchResearchSponsorshipAdmin,
    saveResearchOffer,
} from 'api/research-sponsorship';
import {
    researchOfferBody,
    researchOfferChanged,
    researchOfferDraft,
    researchOfferError,
    researchKeepsSlotsOff,
} from 'utils/research-sponsor';

import Typo from 'components/Typo';
import SidebarLayout from 'layouts/SidebarLayout';
import Sidescreen from 'components/Sidescreen';
import PostAlert from 'components/PostAlert';
import CountDown from 'components/Alert/Countdown';
import Deadline from 'components/Alert/Deadline';
import Responses from 'components/Alert/Responses';
import AdminButtons from 'components/Alert/AdminButtons';
import Button from 'components/Button';
import AddResponse from 'components/Alert/AddResponse';
import PopupConfirm from 'components/popUps/PopupConfirm';
import NewsAlertLogo from 'components/Svg/NewsAlertLogo';
import Image from 'next/image';
import styles from 'styles/modules/newsAlert/Detail.module.scss';
import Banner from '../../components/Alert/Banner';
import Quote from 'components/Svg/Quote2';
import copyFormatted from 'utils/copyFormattedHTML';
import ResponseQuote from 'components/Alert/ResponseQuote';
import ResponseArticle from 'components/Alert/ResponseArticle';
import Newsroom from 'components/Alert/Newsroom';
import Story, { storyUrl } from 'components/Alert/Newsroom/Story';
import RawBadge from 'components/Alert/Newsroom/RawBadge';
import SourceCredit from 'components/Alert/Newsroom/SourceCredit';
import DirectoryTopicsPicker from 'components/Alert/Newsroom/DirectoryTopicsPicker';
import RawTags from 'components/RawTags';
import RanThis from 'components/Alert/RanThis';
import ResearchSponsorBox, {
    ResearchCredit,
    ResearchStrip,
} from 'components/Alert/ResearchSponsorBox';
import RawBank from 'components/Raw/RawBank';
import {
    firstImage,
    formatDateTime,
    isReleasedRaw,
    youtubeIds,
} from 'utils/newsroom';
import { leadImage, leadImageOg } from 'utils/lead-image';
import { pickSource } from 'utils/source-page';
import { writesAlerts } from 'utils/subscriptionAccess';
import {
    alertTemplate,
    alertTemplateError,
    filledQuestions,
} from 'utils/alert-template';

const researchCopy = news_alerts.research_sponsor;
const DAY = 24 * 60 * 60 * 1000;

const NewsAlert = ({ alert, responses, schema, videoDates }) => {
    const dispatch = useDispatch();
    const responsesState = useSelector(({ alerts }) => alerts.alertResponses);
    const { alert: alertState } = useSelector(({ alerts }) => alerts);
    const {
        isAuthenticated,
        isAdmin,
        company,
        isJournalist,
        isContributingEditor,
        isFTEditor,
        id: userId,
    } = useSelector(authSelector);
    const { data: ownCompany } = useSelector(companySelector);
    const plan = isAuthenticated ? ownCompany?.plan || null : null;
    const router = useRouter();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(alert);
    const [responsesList, setResponsesList] = useState(responses);
    const [edit, setEdit] = useState(false);
    const [dateValid, setDateValid] = useState(true);
    const [timeValid, setTimeValid] = useState(true);
    const [deadline, setDeadline] = useState(data?.deadline);
    const [title, setTitle] = useState(data?.title);
    const [body, setBody] = useState(data?.body);
    const [template, setTemplate] = useState(alertTemplate(data));
    // Admins may move the alert to a new address while editing; the old one
    // keeps working as a redirect.
    const [slug, setSlug] = useState(data?.slug);
    const [slugStatus, setSlugStatus] = useState({ ok: true, checking: false });
    const [updateError, setUpdateError] = useState(null);
    // Admin edit: who last saved the alert from this form, and when. The cached page has no names.
    const [lastEdit, setLastEdit] = useState(null);
    const saving = useRef(false);
    // The cached page may carry an old version: the form reloads the alert when opened, but
    // only fills itself while the user has not started editing.
    const touched = useRef(false);
    const [formKey, setFormKey] = useState(0);
    const [conflict, setConflict] = useState(false);
    const [removeError, setRemoveError] = useState(null);
    const [expertises, setExpertises] = useState(data?.expertise);
    const [source, setSource] = useState(pickSource(data));
    const [copied, setCopied] = useState(false);
    const [articleCopied, setArticleCopied] = useState(false);
    const [profilesWithAddresses, setProfilesWithAddresses] = useState({});
    // While an admin edits the story, the published version is hidden.
    const [editingStory, setEditingStory] = useState(false);
    // Admin edit: the saved story sponsorship offer and its draft; null until loaded.
    const [researchRow, setResearchRow] = useState(null);
    const [research, setResearch] = useState(null);
    const [researchLoadError, setResearchLoadError] = useState(null);
    const researchRequest = useRef(0);
    // Remounts the public box after an admin change, so it fetches the new status.
    const [researchVersion, setResearchVersion] = useState(0);

    // The page is cached HTML built without a user, so the signed-in user's own
    // company (which carries their plan) and their experts are fetched here
    // instead of in getStaticProps.
    useEffect(() => {
        if (!isAuthenticated || !company) {
            return;
        }
        (async () => {
            await dispatch(fetchCompanyBySlug(company));
            await dispatch(fetchExperts());
        })();
    }, [isAuthenticated, company]);

    // The cached page is built logged out, so its responses carry no firm email or phone. A
    // signed-in member fetches them again; a plain request, as a failed fetchResponses would
    // clear the alert and blank the page.
    useEffect(() => {
        if (!isAuthenticated || !alertState?.id) {
            return;
        }
        getReq(`/alerts/${alertState.id}/responses?size=${MAX_PAGE_SIZE}`)
            .then(({ data }) => {
                if (Array.isArray(data?.data?.items)) {
                    dispatch(fetchResponses.fulfilled(data.data.items));
                }
            })
            .catch(() => {});
    }, [isAuthenticated, alertState?.id]);

    // ?author=0 hides the alert author and cannot reach a cached page.
    useEffect(() => {
        if (router.query.author === '0') {
            dispatch(
                fetchAlertBySlug({ slug: router.query.slug, author: '0' })
            );
        }
    }, [router.query.author, router.query.slug]);

    // The cached page is built logged out, so a journalist's name is never in it. A signed-in
    // member asks the API again, which names the journalist only to paid plans and admins.
    // Only the author is taken over, and a failure leaves the page as it was.
    useEffect(() => {
        if (
            !isAuthenticated ||
            !alertState?.author?.isAnonymised ||
            router.query.author === '0'
        ) {
            return;
        }
        getReq(`/alerts/slug/${router.query.slug}`)
            .then(({ data }) => {
                if (data?.data?.author && !data.data.author.isAnonymised) {
                    dispatch(revealAlertAuthor(data.data));
                }
            })
            .catch(() => {});
    }, [isAuthenticated, alertState?.id, alertState?.author?.isAnonymised]);

    // Storify greys out the other sponsor slots while research sponsorship holds them,
    // including a sold sponsor an admin hid, which the public view leaves out.
    const researchAlertId =
        isAdmin === true &&
        alertState &&
        (!alertState.type || alertState.type === 'ALERT')
            ? alertState.id
            : null;
    useEffect(() => {
        if (!researchAlertId) {
            return;
        }
        let current = true;
        fetchResearchSponsorshipAdmin(researchAlertId)
            .then((row) => current && setResearchRow((shown) => shown ?? row))
            .catch(() => {});
        return () => {
            current = false;
        };
    }, [researchAlertId]);

    useEffect(() => {
        setData(alertState);
        if (alertState) {
            setDeadline(alertState.deadline);
            setTitle(alertState.title);
            setBody(alertState.body);
            setTemplate(alertTemplate(alertState));
            setExpertises(alertState.expertise);
            setSource(pickSource(alertState));
        }
    }, [alertState]);

    const isFTEditorByRole = (author) => {
        if (!author?.roles || !Array.isArray(author.roles)) return false;
        return author.roles.some((role) => role.name === 'FT_EDITOR');
    };

    // FT Adviser branding names the publication an anonymised journalist writes for.
    const isCreatedByFTEditor =
        !data?.author?.isAnonymised &&
        (data?.author?.isFTEditor || isFTEditorByRole(data?.author));

    useEffect(() => {
        if (responsesState) {
            setResponsesList(responsesState);

            // Fetch addresses for all profiles
            const fetchAddressesForProfiles = async () => {
                const addressesMap = {};
                const uniqueProfileIds = [
                    ...new Set(responsesState.map((r) => r.profile.id)),
                ];

                for (const profileId of uniqueProfileIds) {
                    try {
                        const res = await getReq(`/profiles/${profileId}`);
                        if (res.status === 200) {
                            addressesMap[profileId] = res.data.data.addresses;
                        }
                    } catch (error) {
                        console.error(
                            `Error fetching profile ${profileId}:`,
                            error
                        );
                    }
                }

                setProfilesWithAddresses(addressesMap);
            };

            if (responsesState.length > 0) {
                fetchAddressesForProfiles();
            }
        }
    }, [responsesState]);
    const [active, setActive] = useState(null);
    const sourceRef = useRef(null);
    const hasStory = Boolean(data?.story?.publishedAt);

    // In-copy credits link to #raw-alert, which has to open the folded raw alert.
    useEffect(() => {
        if (!hasStory) {
            return;
        }
        const openOnHash = () => {
            if (window.location.hash === '#raw-alert' && sourceRef.current) {
                sourceRef.current.open = true;
            }
        };
        openOnHash();
        window.addEventListener('hashchange', openOnHash);
        return () => window.removeEventListener('hashchange', openOnHash);
    }, [hasStory]);

    if (!data) {
        return null;
    }

    const handleCloseArticle = async () => {
        setLoading(true);
        await dispatch(closeAlert(data.id));
        setDeadline(new Date());
        setData({ ...data, deadline: new Date(), status: 'inactive' });

        setLoading(false);
    };

    // The API refuses (409) while a research sponsorship is reserved or sold; say so
    // instead of leaving the page as if the alert were gone.
    const handleRemoveArticle = async () => {
        setActive(false);
        setRemoveError(null);
        const result = await dispatch(removeAlert(data.id));
        if (removeAlert.rejected.match(result)) {
            setRemoveError(
                result.payload?.message || 'The alert could not be deleted.'
            );
            return;
        }
        await router.push(`/news-alerts`);
    };

    // Only news alerts can offer it; this page shows nothing else today.
    const canOfferResearch =
        isAdmin === true && (!data.type || data.type === 'ALERT');

    const markTouched = () => {
        if (edit) touched.current = true;
    };

    const fillForm = (alert) => {
        setDeadline(alert.deadline);
        setTitle(alert.title);
        setBody(alert.body);
        setTemplate(alertTemplate(alert));
        setSlug(alert.slug);
        setSource(pickSource(alert));
    };

    const loadLatest = (isCurrent, canFill) =>
        getReq(`/alerts/slug/${data.slug}`).then(({ data: res }) => {
            const latest = res?.data;
            if (!latest || !isCurrent()) return false;
            if (isAdmin === true) setLastEdit(latest);
            if (!canFill()) return false;
            const patch = {
                ...pickSource(latest),
                deadline: latest.deadline,
                title: latest.title,
                body: latest.body,
                intro: latest.intro,
                questions: latest.questions,
                slug: latest.slug,
                version: latest.version,
            };
            // The effect on alertState refills the form; a stale store copy would bring the 409 back.
            dispatch(updateAlert.fulfilled(patch));
            setData((current) => ({ ...current, ...patch }));
            fillForm(latest);
            // Remounts the editors, which read their value only on mount.
            setFormKey((key) => key + 1);
            return true;
        });

    const handleLoadLatest = () => {
        const request = researchRequest.current;
        const current = () => request === researchRequest.current;
        const failed = () =>
            current() &&
            setUpdateError('The latest version could not be loaded.');
        loadLatest(current, current)
            .then((loaded) => {
                if (!loaded) return failed();
                setConflict(false);
                setUpdateError(null);
            })
            .catch(failed);
    };

    const handleEditArticle = () => {
        setEdit(!edit);
        setConflict(false);
        touched.current = false;
        setResearch(null);
        setResearchLoadError(null);
        const request = ++researchRequest.current;
        // Ignored once Edit is closed or reopened: it belongs to that session.
        const current = () => request === researchRequest.current;
        if (!edit && canOfferResearch) {
            fetchResearchSponsorshipAdmin(data.id)
                .then((row) => current() && showResearchRow(row))
                .catch(
                    () =>
                        current() &&
                        setResearchLoadError(researchCopy.admin_load_failed)
                );
        }
        setLastEdit(null);
        if (!edit) {
            // On failure the form keeps the page's copy.
            loadLatest(current, () => !touched.current).catch(() => {});
        }
        fillForm(data);
        setUpdateError(null);
    };

    const handleUpdateArticle = async () => {
        // A second click before the first save returns would send the version that save replaces.
        if (saving.current) return;
        if (isAdmin && !slugStatus.ok) {
            setUpdateError(
                slugStatus.checking
                    ? news_alerts.create_alert.screen_1.slug_checking
                    : news_alerts.create_alert.screen_1.slug_blocked
            );
            return;
        }
        const researchPending =
            canOfferResearch && !research && !researchLoadError;
        const templateError =
            (template &&
                alertTemplateError(
                    template,
                    isAdmin === true,
                    data.intro,
                    data.questions
                )) ||
            (research &&
                researchOfferError(research, researchRow?.researchDetail)) ||
            (researchPending && researchCopy.admin_loading);
        if (templateError) {
            setUpdateError(templateError);
            return;
        }
        saving.current = true;
        // A reload still in flight must not change the version this save is checked against.
        touched.current = true;
        setLoading(true);
        setUpdateError(null);
        setConflict(false);
        const researchChanged =
            research && researchOfferChanged(research, researchRow);
        const updatedAlert = {
            ...data,
            ...source,
            deadline: moment(deadline).toISOString(),
            body,
            title,
            ...(template && {
                intro: template.intro.trim(),
                questions: filledQuestions(template.questions),
            }),
        };
        delete updatedAlert.expertise;
        // `slug` stays the current address so the thunk revalidates it; the
        // new one travels as newSlug and only an admin's is honoured.
        if (isAdmin && slug && slug.trim() !== data.slug) {
            updatedAlert.newSlug = slug.trim();
        }
        const res = await dispatch(updateAlert(updatedAlert));
        if (updateAlert.rejected.match(res)) {
            const alertError = apiErrorMessage(
                res.payload,
                'The alert could not be saved.'
            );
            setUpdateError(alertError);
            // A taken address is a 409 too; only a stale copy offers the latest version.
            setConflict(
                res.payload?.statusCode === 409 &&
                    alertError.includes('after you opened it')
            );
            saving.current = false;
            setLoading(false);
            return;
        }
        const saved = res.payload || {};
        // A templated alert's body is rendered by the API from intro and questions.
        const savedBody = template ? saved.body ?? body : body;
        setData({
            ...data,
            ...pickSource(saved),
            deadline: moment(deadline).toISOString(),
            body: savedBody,
            intro: saved.intro ?? data.intro,
            questions: saved.questions ?? data.questions,
            title,
            status: saved.status ?? data.status,
            slug: saved.slug ?? data.slug,
            // The next save is checked against this one.
            version: saved.version ?? data.version,
        });
        if (saved.lastEditedByName) setLastEdit(saved);
        setSlug(saved.slug ?? data.slug);
        setBody(savedBody);
        setTemplate(alertTemplate(saved.questions ? saved : data));
        setDeadline(deadline);
        const researchSaveError = researchChanged ? await saveResearch() : null;
        const researchError =
            researchSaveError &&
            `${researchCopy.admin_saved_not_research} ${researchSaveError}`;
        if (researchChanged) {
            refreshResearch();
        }
        saving.current = false;
        setLoading(false);
        if (researchError) {
            setUpdateError(researchError);
        } else {
            setEdit(false);
        }
        if (saved.slug && saved.slug !== data.slug) {
            router.replace(alertPath(saved.slug));
        }
    };

    const showResearchRow = (row) => {
        setResearchRow(row);
        setResearch(researchOfferDraft(row));
    };

    const saveResearch = async () => {
        try {
            showResearchRow(
                await saveResearchOffer(data.id, researchOfferBody(research))
            );
            return null;
        } catch (err) {
            return apiErrorMessage(
                err?.response?.data,
                researchCopy.admin_save_failed
            );
        }
    };

    // The public view carries only a sold credit; the box asks for the offer itself.
    const refreshResearch = () =>
        fetchResearchSponsorship(data.id)
            .then((researchSponsorship) => {
                setData((current) => ({ ...current, researchSponsorship }));
                setResearchVersion((version) => version + 1);
            })
            .catch(() => {});

    const isFTEditorAlert =
        data?.author?.isFTEditor && !data?.author?.isAnonymised;

    // A published story (Storify) sits above the raw alert and takes over the
    // page's title, description and Open Graph data. The URL stays the same.
    const story = data.story?.publishedAt ? data.story : null;
    const seo = story
        ? {
              title: story.seoTitle || story.headline,
              description:
                  story.metaDescription ||
                  truncate(tagRemove(story.body), 160, true),
          }
        : {
              title: data.title,
              description: truncate(tagRemove(data.body), 160, true),
          };
    const lead = story && leadImage(story);
    const storyImage = story ? firstImage(story.body) : null;
    const shareImage = lead
        ? leadImageOg(lead)
        : storyImage && { url: storyImage };

    const possibleToAddResponse =
        isAuthenticated &&
        !isAdmin &&
        !isJournalist &&
        !isFTEditor &&
        (isFTEditorAlert || data.responsesLeft > 0) &&
        data.status === 'active' &&
        responsesList.filter((item) => item.profile.slug === company).length ===
            0;

    // A published story leads; the alert and responses it came from fold away under it.
    const SourceWrapper = story ? 'details' : 'div';
    // The offer sits at the bottom of the alert, as briefed, for logged-in experts only
    // (admins too, to check it), and never once a story leads the page. Once sold, the
    // sponsor is credited at the top instead: under the alert's title, or in the story's byline.
    const researchSold = data.researchSponsorship?.status === 'sold';
    const researchBox = !researchSold &&
        !story &&
        data.status === 'active' &&
        isAuthenticated &&
        (isAdmin || !isJournalist) && (
            <ResearchSponsorBox key={researchVersion} alertId={data.id} />
        );
    const researchCredit = researchSold &&
        !story &&
        data.researchSponsorship.sponsorName && (
            <ResearchCredit
                name={data.researchSponsorship.sponsorName}
                website={data.researchSponsorship.sponsorWebsite}
            />
        );
    // The price under the headline while the offer is for sale, to everyone who could buy it:
    // logged-out visitors go through the login, which sends them back to the box.
    const researchStrip = !researchSold &&
        !story &&
        data.status === 'active' &&
        (!isAuthenticated || isAdmin || !isJournalist) && (
            <ResearchStrip
                key={researchVersion}
                alertId={data.id}
                deadline={deadline}
                loginHref={
                    isAuthenticated
                        ? null
                        : `/login?redirect=${encodeURIComponent(
                              `${alertPath(data.slug)}#research-sponsor`
                          )}`
                }
            />
        );
    const deadlineComponent = edit ? (
        <Deadline
            initialDate={deadline}
            setDeadline={setDeadline}
            setDateValid={setDateValid}
            setTimeValid={setTimeValid}
        />
    ) : (
        <CountDown
            className={styles.timer}
            close={handleCloseArticle}
            status={data.status}
            createdAt={data.createdAt}
            deadline={deadline}
            isFTEditor={isCreatedByFTEditor}
        />
    );

    const handleQuoteAll = () => {
        let result = `
        <meta charset="utf-8">
        `;

        responsesList.forEach((r) => {
            const profileAddresses = profilesWithAddresses[r.profile.id];
            result += ReactDOMServer.renderToString(
                <ResponseQuote
                    company={{
                        name: r?.profile?.employer || r?.profile?.name,
                        expertName: r?.expert?.name || r?.profile?.name,
                        expertPosition:
                            r?.expert?.position || r?.profile?.position,
                        website: r?.profile?.website,
                        slug: r?.profile?.slug,
                        deactivated: r?.profile?.deactivated,
                        city:
                            profileAddresses && profileAddresses.length > 0
                                ? profileAddresses[0].city
                                : null,
                    }}
                    response={{ body: r.body }}
                    alertSlug={router.query.slug}
                    planType={r?.author?.plan?.type}
                />
            );
            result += '<br/><br/>';
        });

        copyFormatted(result);
        setCopied(true);

        setTimeout(() => {
            setCopied(false);
        }, 1000);
    };

    const handleCopyArticle = () => {
        const result = `
        <meta charset="utf-8">
        ${ReactDOMServer.renderToString(
            <ResponseArticle body={body} title={title} />
        )}
        `;

        copyFormatted(result);
        setArticleCopied(true);

        setTimeout(() => {
            setArticleCopied(false);
        }, 1000);
    };

    return (
        <SidebarLayout isAuthenticated={isAuthenticated}>
            <NextSeo
                {...SEO}
                titleTemplate={
                    story ? pageTitle(seo.title) : articleTitle(seo.title)
                }
                description={seo.description}
                canonical={alertUrl(data.slug)}
                // Daily Intel repeats the same words each day and a service announcement is a notice to
                // members: neither is a page for Google (both are left out of the sitemap too), nor is an
                // alert an editor took out of Google.
                noindex={
                    /^newspage-intel-/.test(data.slug ?? '') ||
                    data.announcementRecipients != null ||
                    Boolean(data.noindex)
                }
                openGraph={
                    story
                        ? {
                              ...SEO.openGraph,
                              type: 'article',
                              title: seo.title,
                              description: seo.description,
                              url: storyUrl(data.slug),
                              article: {
                                  publishedTime: story.publishedAt,
                                  modifiedTime:
                                      story.updatedAt || story.publishedAt,
                              },
                              ...(shareImage ? { images: [shareImage] } : {}),
                          }
                        : {
                              description: seo.description,
                              url: alertUrl(data.slug),
                          }
                }
                {...(lead && {
                    twitter: {
                        ...SEO.twitter,
                        cardType: 'summary_large_image',
                    },
                    additionalMetaTags: [
                        { name: 'twitter:image', content: shareImage.url },
                        { name: 'twitter:image:alt', content: shareImage.alt },
                    ],
                })}
            />
            {schema && !story && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: jsonLd(schema) }}
                />
            )}

            <PopupConfirm
                active={active}
                confirmTitle={'Confirm'}
                onConfirm={handleRemoveArticle}
                onCancel={() => setActive(false)}
            />

            <div className={styles.container}>
                {removeError && <p className={styles.error}>{removeError}</p>}
                {isAdmin === true ||
                isJournalist === true ||
                isFTEditor === true ||
                (isContributingEditor === true && data?.authorId === userId) ||
                (writesAlerts(plan?.type) && data?.authorId === userId) ? (
                    <AdminButtons
                        status={data.status}
                        handleEditArticle={handleEditArticle}
                        handleCloseArticle={handleCloseArticle}
                        handleUpdateArticle={handleUpdateArticle}
                        loading={loading}
                        edit={edit}
                        setActive={setActive}
                        timeValid={timeValid}
                        dateValid={dateValid}
                        isAdmin={isAdmin}
                        isContributingEditor={isContributingEditor}
                        isFTEditor={isFTEditor}
                        plan={plan}
                        alertId={data.id}
                        noindex={Boolean(data.noindex)}
                        onNoindexChange={(noindex) =>
                            setData((current) => ({ ...current, noindex }))
                        }
                    />
                ) : null}
                {/* Next to Update: the story sits between it and the form's own errors. */}
                {edit && conflict && (
                    <div className={styles.conflict}>
                        <p className={styles.error}>{updateError}</p>
                        <Button
                            theme={'secondary'}
                            title={'Load the latest version'}
                            onClick={handleLoadLatest}
                        />
                    </div>
                )}

                {isAdmin === true && (
                    <Newsroom
                        alert={data}
                        researchOn={
                            // The admin row covers an offer still for sale; the public view carries only a sale.
                            researchKeepsSlotsOff(researchRow) ||
                            Boolean(data.researchSponsorship)
                        }
                        responsesCount={responsesList.length}
                        onAlertChange={(patch) =>
                            setData((current) => ({ ...current, ...patch }))
                        }
                        onStoryChange={(published) =>
                            setData((current) => ({
                                ...current,
                                story: published?.publishedAt
                                    ? published
                                    : null,
                            }))
                        }
                        onEditingChange={setEditingStory}
                    />
                )}

                {story && !editingStory && (
                    <Story
                        story={story}
                        alert={data}
                        responses={responsesList}
                        videoDates={videoDates}
                    />
                )}

                <SourceWrapper
                    ref={sourceRef}
                    onKeyDownCapture={markTouched}
                    onPointerDownCapture={markTouched}
                    onInputCapture={markTouched}
                    className={story ? styles.source : undefined}
                    open={
                        story && (edit || possibleToAddResponse)
                            ? true
                            : undefined
                    }
                >
                    {story && (
                        <summary className={styles.sourceToggle}>
                            {`Show the original alert and ${
                                responsesList.length
                            } ${pluralize(responsesList.length, 'response')}`}
                        </summary>
                    )}
                    <div className={['mb-l', styles.logo].join(' ')}>
                        <div className={styles.logoContainer}>
                            {isCreatedByFTEditor && (
                                <Image
                                    src="/img/news-story/ft_logo.webp"
                                    alt="FT Logo"
                                    width={134}
                                    height={34}
                                    className={styles.ftLogo}
                                />
                            )}
                            {isCreatedByFTEditor && (
                                <div className={styles.logoDivider} />
                            )}
                            <NewsAlertLogo width={168} height={37} />
                        </div>

                        <div
                            onClick={
                                isAuthenticated && !articleCopied
                                    ? () => handleCopyArticle()
                                    : null
                            }
                            className={[
                                styles['quote-all'],
                                !isAuthenticated && 'loginLocked',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            data-hint={
                                isAuthenticated ? undefined : common.loginToCopy
                            }
                            style={{ margin: 'unset' }}
                        >
                            <p>
                                {!articleCopied
                                    ? 'Copy article'
                                    : 'Copied to clipboard'}
                            </p>{' '}
                            <Quote fill="#fff" />
                        </div>

                        {isReleasedRaw(data) && (
                            <div
                                className={[
                                    styles.logoContainer,
                                    styles.rawMeta,
                                ].join(' ')}
                            >
                                <RawBadge alert={data} />
                                {data.seededBy && (
                                    <SourceCredit seededBy={data.seededBy} />
                                )}
                            </div>
                        )}
                    </div>

                    {/* One ad per page: a story's own sponsor or a sold research sponsorship replaces the campaign banner. */}
                    {!liveSponsor(story) &&
                        data.researchSponsorship?.status !== 'sold' && (
                            <Banner campaign={alert?.campaign} />
                        )}

                    {edit &&
                        lastEdit?.lastEditedByName &&
                        lastEdit?.lastEditedAt && (
                            <p className={styles.lastEdited}>
                                Last edited by {lastEdit.lastEditedByName},{' '}
                                {formatDateTime(lastEdit.lastEditedAt)}
                            </p>
                        )}
                    {edit && updateError && !conflict && (
                        <p className={styles.error}>{updateError}</p>
                    )}
                    <PostAlert
                        editorKey={formKey}
                        titleVariant={story ? 'h2' : 'h1'}
                        author={data?.author ? data.author : false}
                        title={title}
                        body={body}
                        edit={edit}
                        deadline={deadlineComponent}
                        setData={{
                            setTitle,
                            setBody,
                            setSlug,
                            setSlugStatus,
                            setTemplate,
                        }}
                        template={template}
                        storedIntro={data.intro}
                        sponsorCredit={researchCredit}
                        sponsorStrip={researchStrip}
                        isAdmin={isAdmin === true}
                        slug={slug}
                        currentSlug={data.slug}
                        alertId={data.id}
                        source={edit ? source : pickSource(data)}
                        setSource={setSource}
                        research={research}
                        researchRow={researchRow}
                        setResearch={setResearch}
                        onResearchRowChange={(row) => {
                            showResearchRow(row);
                            refreshResearch();
                        }}
                        researchError={researchLoadError}
                    />

                    {/* The open story editor has its own picker; each one loads the picks when it mounts. */}
                    {edit && canOfferResearch && !editingStory && (
                        <DirectoryTopicsPicker
                            alertId={data.id}
                            published={!!story?.publishedAt}
                        />
                    )}

                    {researchBox}

                    <div
                        className={[
                            'mt-xxl',
                            styles.responses,
                            isCreatedByFTEditor ? styles.ftEditor : '',
                        ].join(' ')}
                    >
                        {possibleToAddResponse && (
                            <>
                                <Typo
                                    variant={'h2'}
                                    className={[
                                        'mb-xs',
                                        styles.responsesTitle,
                                    ].join(' ')}
                                >
                                    {`${data?.responsesLeft} ${pluralize(
                                        data?.responsesLeft,
                                        'response'
                                    )} left, be one of them`}
                                </Typo>
                                {isFTEditorAlert && (
                                    <Typo
                                        variant={'h3'}
                                        className={[
                                            'mb-xxl',
                                            styles.responsesTitle,
                                        ].join(' ')}
                                    >
                                        This is an FT Adviser-sponsored News
                                        Alert. Anyone can respond. For free. No
                                        credits will be used.
                                    </Typo>
                                )}
                                <AddResponse
                                    key={data.id}
                                    alertId={data.id}
                                    alertData={data}
                                />
                            </>
                        )}

                        {!possibleToAddResponse && (
                            <>
                                <Typo
                                    variant={'h2'}
                                    className={[
                                        'mb-xxl',
                                        styles.responsesTitle,
                                    ].join(' ')}
                                >
                                    {`${responsesList.length || 0} ${pluralize(
                                        data?.responses,
                                        'response'
                                    )} from the Newspage community`}

                                    <div
                                        onClick={
                                            isAuthenticated
                                                ? handleQuoteAll
                                                : null
                                        }
                                        className={[
                                            styles['quote-all'],
                                            !isAuthenticated && 'loginLocked',
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        data-hint={
                                            isAuthenticated
                                                ? undefined
                                                : common.loginToCopy
                                        }
                                    >
                                        <p>
                                            {!copied
                                                ? 'Copy all'
                                                : 'Copied to clipboard'}
                                        </p>{' '}
                                        <Quote fill="#fff" />
                                    </div>
                                </Typo>

                                <Responses
                                    id={data.id}
                                    status={data.status}
                                    list={responsesList}
                                    alertSlug={router.query.slug}
                                />
                            </>
                        )}

                        {isAdmin === true ? (
                            <>
                                <br />
                                <br />
                                <Typo variant={'h2'}>{`Expertises:`}</Typo>
                                <br />
                                <br />
                                {expertises && expertises.length !== 0 ? (
                                    <RawTags tags={expertises} />
                                ) : (
                                    'Sent to all'
                                )}
                            </>
                        ) : null}
                    </div>
                </SourceWrapper>

                {isJournalist === true && data.rawAt && (
                    <RawBank alertId={data.id} />
                )}
                {isJournalist === true && <RanThis alertId={data.id} />}

                {/*{!isAdmin && isAuthenticated && <PremiumBanner />}*/}
            </div>

            <Sidescreen
                onUpdate={(val) => {
                    dispatch(handleHideSidescreen());
                }}
            />
        </SidebarLayout>
    );
};

export const getStaticPaths = () => ({ paths: [], fallback: 'blocking' });

export const getStaticProps = wrapper.getStaticProps(
    (store) =>
        async ({ params }) => {
            const result = await store.dispatch(
                fetchAlertBySlug({ slug: params.slug })
            );
            if (fetchAlertBySlug.rejected.match(result)) {
                return notFoundOrThrow(result.payload ?? result.error);
            }
            const alert = store.getState().alerts.alert;
            if (!alert) {
                return notFound();
            }

            // An address the alert used to have: the API resolved it through
            // its slug history, so send the visitor (and Google) on to the
            // current one. 301, not Next's default 308, is what the SEO brief
            // asks for. Cached like a page; the backend revalidates the old
            // path whenever the slug changes again.
            if (alert.slug && alert.slug !== params.slug) {
                return {
                    redirect: {
                        destination: alertPath(alert.slug),
                        statusCode: 301,
                    },
                    revalidate: 86400,
                };
            }

            await store.dispatch(fetchResponses(alert.id));
            const responses = publicResponses(
                store.getState().alerts.alertResponses
            );

            const videoDates = alert.story?.publishedAt
                ? await youtubeUploadDates(alert.story.body)
                : {};
            // A video YouTube gave no date for carries the story's; ask again
            // soon, but only for a day, so a YouTube that never answers our
            // servers doesn't keep the page regenerating every five minutes.
            const videosDated =
                Date.now() - new Date(alert.story?.publishedAt) > DAY ||
                youtubeIds(
                    alert.story?.publishedAt ? alert.story.body : ''
                ).every((id) => videoDates[id]);

            return {
                props: {
                    alert: publicAlert(alert),
                    responses,
                    // a published story renders its own NewsArticle instead
                    schema: alert.story?.publishedAt
                        ? null
                        : buildNewsArticleSchema(
                              alert,
                              responses,
                              process.env.HOST
                          ),
                    videoDates,
                },
                revalidate: sponsorRevalidate(
                    alert.story,
                    videosDated
                        ? alertRevalidate(alert.deadline)
                        : Math.min(alertRevalidate(alert.deadline), 300)
                ),
            };
        }
);

// No wrapper.withRedux here — _app.js already wraps the tree, and on the server
// a page-level wrap gets a second store that never receives HYDRATE.
export default NewsAlert;
